import { CLAW_DEFAULT_AI_MODEL, CLAW_DEFAULT_AI_PROVIDER } from '../constants/clawDefaults.js';

const ID_PATTERN = /^[a-z][a-z0-9.-]*\.[a-z][a-z0-9.-]*$/;

export function emptyPluginDraft(overrides = {}) {
  return {
    id: '',
    version: '0.1.0',
    name: '',
    description: '',
    defaultEnabled: true,
    requiresPlan: '',
    capabilityId: '',
    allowedHosts: '',
    tools: [],
    skills: [],
    appPanels: [],
    stubCapabilities: [],
    ...overrides,
  };
}

export const APP_PANEL_HTML_MAX = 200000;
const OUTPUT_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

export const APP_PANEL_STARTER_HTML = `<style>
  body { font: 14px system-ui, sans-serif; margin: 12px; color: #0f172a; }
  body.dark { color: #e2e8f0; }
  li { margin: 4px 0; }
</style>
<ul id="items"><li>Waiting for data…</li></ul>
<script>
  interviewflow.onData(function (data, msg) {
    document.body.classList.toggle('dark', msg.theme && msg.theme.mode === 'dark');
    var items = Array.isArray(data) ? data : (data && data.items) || [];
    var list = document.getElementById('items');
    list.innerHTML = '';
    items.forEach(function (item) {
      var li = document.createElement('li');
      li.textContent = typeof item === 'string' ? item : (item.title || item.name || JSON.stringify(item));
      list.appendChild(li);
    });
    if (!items.length) list.innerHTML = '<li>No items yet.</li>';
  });
</script>`;

export function emptyAppPanelDraft() {
  return {
    id: '',
    name: '',
    description: '',
    outputKey: 'items',
    height: 360,
    html: APP_PANEL_STARTER_HTML,
  };
}

export function slugFromPluginId(id) {
  const parts = String(id || '').trim().split('.');
  return parts[parts.length - 1] || 'plugin';
}

export function emptyToolDraft() {
  return {
    id: '',
    name: '',
    description: '',
    source: 'extension',
    httpJson: '',
    inputSchemaJson: '',
    outputSchemaJson: '',
  };
}

function toolDraft(t) {
  return {
    id: t.id || '',
    name: t.name || t.id || '',
    description: t.description || '',
    source: t.source || 'extension',
    httpJson: t.http ? JSON.stringify(t.http, null, 2) : '',
    inputSchemaJson: t.inputSchema ? JSON.stringify(t.inputSchema, null, 2) : '',
    outputSchemaJson: t.outputSchema ? JSON.stringify(t.outputSchema, null, 2) : '',
  };
}

export function parseAllowedHosts(text) {
  return [...new Set(String(text || '').split(/[\s,]+/).map((h) => h.trim().toLowerCase()).filter(Boolean))];
}

/** Same matching as the server: exact host, or `*.example.com` for subdomains. */
function hostAllowed(host, patterns) {
  const h = String(host || '').toLowerCase();
  return patterns.some((p) => (p.startsWith('*.') ? h.endsWith(p.slice(1)) && h !== p.slice(2) : h === p));
}

export function emptyStubCapabilityDraft() {
  return {
    id: '',
    kind: 'flow-node',
    type: '',
    name: '',
    description: '',
  };
}

export function emptySkillDraft() {
  return {
    id: '',
    name: '',
    description: '',
    systemPrompt: '',
    allowedTools: [],
    defaultModel: CLAW_DEFAULT_AI_MODEL,
    defaultProvider: CLAW_DEFAULT_AI_PROVIDER,
    temperature: 0.25,
    maxTokens: 2048,
  };
}

export function draftFromManifest(manifest) {
  if (!manifest || typeof manifest !== 'object') return emptyPluginDraft();
  const bundle = (manifest.capabilities || []).find((c) => c.kind === 'mcp-tool-bundle')
    || (manifest.capabilities || []).find((c) => c.kind === 'mcp-tool');
  const tools = bundle?.tools?.length
    ? bundle.tools.map(toolDraft)
    : bundle?.tool
      ? [toolDraft(bundle.tool)]
      : [];

  const stubKinds = new Set(['flow-node', 'connector', 'app-control']);
  const isAppPanel = (c) => c.kind === 'app-control' && typeof c.panel?.html === 'string';
  const appPanels = (manifest.capabilities || []).filter(isAppPanel).map((c) => ({
    id: c.id || '',
    name: c.name || '',
    description: c.description || '',
    outputKey: c.panel.outputKey || '',
    height: c.panel.height || 360,
    html: c.panel.html,
  }));
  const stubs = (manifest.capabilities || [])
    .filter((c) => stubKinds.has(c.kind) && !isAppPanel(c))
    .map((c) => ({
      id: c.id || '',
      kind: c.kind || 'flow-node',
      type: c.type || '',
      name: c.name || c.ui?.label || '',
      description: c.description || '',
    }));

  return emptyPluginDraft({
    id: manifest.id || '',
    version: manifest.version || '0.1.0',
    name: manifest.name || '',
    description: manifest.description || '',
    defaultEnabled: manifest.tenantPolicy?.defaultEnabled !== false,
    requiresPlan: manifest.tenantPolicy?.requiresPlan || '',
    capabilityId: bundle?.id || '',
    allowedHosts: (manifest.permissions?.allowedHosts || []).join(', '),
    tools,
    skills: (manifest.skills || []).map((s) => ({
      id: s.id || '',
      name: s.name || '',
      description: s.description || '',
      systemPrompt: s.systemPrompt || '',
      allowedTools: Array.isArray(s.allowedTools) ? [...s.allowedTools] : [],
      defaultModel: s.defaultModel || CLAW_DEFAULT_AI_MODEL,
      defaultProvider: s.defaultProvider || CLAW_DEFAULT_AI_PROVIDER,
      temperature: s.temperature ?? 0.25,
      maxTokens: s.maxTokens ?? 2048,
    })),
    appPanels,
    stubCapabilities: stubs,
  });
}

function parseOptionalJson(text, label, errors) {
  const raw = String(text || '').trim();
  if (!raw) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    errors.push(`${label}: invalid JSON schema`);
    return undefined;
  }
}

export function manifestFromDraft(draft) {
  const schemaErrors = [];
  const capabilities = [];
  const cleanTools = (draft.tools || [])
    .filter((t) => String(t.id || '').trim() && String(t.name || '').trim())
    .map((t) => {
      const tool = {
        id: String(t.id).trim(),
        name: String(t.name).trim(),
        description: String(t.description || '').trim() || undefined,
        source: t.source || 'extension',
      };
      if (tool.source === 'http') {
        const http = parseOptionalJson(t.httpJson, `Tool ${tool.id} HTTP request`, schemaErrors);
        if (http) tool.http = http;
        else if (!String(t.httpJson || '').trim()) schemaErrors.push(`Tool ${tool.id}: HTTP tools need an HTTP request (url, method, query)`);
      }
      const inputSchema = parseOptionalJson(t.inputSchemaJson, `Tool ${tool.id} input`, schemaErrors);
      const outputSchema = parseOptionalJson(t.outputSchemaJson, `Tool ${tool.id} output`, schemaErrors);
      if (inputSchema) tool.inputSchema = inputSchema;
      if (outputSchema) tool.outputSchema = outputSchema;
      return tool;
    });

  if (cleanTools.length > 0) {
    const capId = String(draft.capabilityId || '').trim() || `${slugFromPluginId(draft.id)}.tools`;
    capabilities.push({
      id: capId,
      kind: cleanTools.length === 1 ? 'mcp-tool' : 'mcp-tool-bundle',
      ...(cleanTools.length === 1
        ? { tool: cleanTools[0] }
        : { tools: cleanTools }),
    });
  }

  (draft.stubCapabilities || [])
    .filter((s) => String(s.id || '').trim() && String(s.kind || '').trim())
    .forEach((s) => {
      const cap = {
        id: String(s.id).trim(),
        kind: String(s.kind).trim(),
        name: String(s.name || s.id).trim(),
        description: String(s.description || 'Declaration stub — requires repo manifest + deploy.').trim(),
        status: 'beta',
      };
      if (s.kind === 'flow-node' && String(s.type || '').trim()) {
        cap.type = String(s.type).trim();
      }
      if (s.kind === 'connector') {
        cap.connectorId = String(s.type || s.id).trim();
      }
      capabilities.push(cap);
    });

  (draft.appPanels || [])
    .filter((p) => String(p.id || '').trim())
    .forEach((p) => {
      const panel = { html: String(p.html || '') };
      if (String(p.outputKey || '').trim()) panel.outputKey = String(p.outputKey).trim();
      const height = Number(p.height);
      if (Number.isFinite(height) && height > 0) panel.height = Math.min(1600, Math.max(120, Math.round(height)));
      capabilities.push({
        id: String(p.id).trim(),
        kind: 'app-control',
        name: String(p.name || p.id).trim(),
        ...(String(p.description || '').trim() ? { description: String(p.description).trim() } : {}),
        panel,
      });
    });

  const skills = (draft.skills || [])
    .filter((s) => String(s.id || '').trim() && String(s.name || '').trim())
    .map((s) => {
      const out = {
        id: String(s.id).trim(),
        name: String(s.name).trim(),
      };
      if (String(s.description || '').trim()) out.description = String(s.description).trim();
      if (String(s.systemPrompt || '').trim()) out.systemPrompt = String(s.systemPrompt).trim();
      if (Array.isArray(s.allowedTools) && s.allowedTools.length) {
        out.allowedTools = s.allowedTools.map(String);
      }
      if (s.defaultModel) out.defaultModel = String(s.defaultModel);
      if (s.defaultProvider) out.defaultProvider = String(s.defaultProvider);
      if (s.temperature != null && s.temperature !== '') out.temperature = Number(s.temperature);
      if (s.maxTokens != null && s.maxTokens !== '') out.maxTokens = Number(s.maxTokens);
      return out;
    });

  const manifest = {
    id: String(draft.id || '').trim(),
    version: String(draft.version || '0.1.0').trim(),
    name: String(draft.name || '').trim(),
    capabilities,
  };

  if (String(draft.description || '').trim()) {
    manifest.description = String(draft.description).trim();
  }
  if (skills.length) manifest.skills = skills;
  const allowedHosts = parseAllowedHosts(draft.allowedHosts);
  if (allowedHosts.length) manifest.permissions = { allowedHosts };

  const tenantPolicy = {};
  if (draft.defaultEnabled === false) tenantPolicy.defaultEnabled = false;
  else tenantPolicy.defaultEnabled = true;
  if (draft.requiresPlan) tenantPolicy.requiresPlan = draft.requiresPlan;
  manifest.tenantPolicy = tenantPolicy;

  if (schemaErrors.length) {
    manifest._schemaParseErrors = schemaErrors;
  }
  return manifest;
}

export function buildPluginPackageForExport(manifest, { author = '', notes = '' } = {}) {
  return {
    packageVersion: 'plugin-package/v1',
    manifest,
    ...(author ? { author } : {}),
    ...(notes ? { notes } : {}),
  };
}

export function parsePluginPackageImport(raw) {
  if (!raw || typeof raw !== 'object') {
    return { manifest: null, error: 'Invalid JSON' };
  }
  if (raw.packageVersion === 'plugin-package/v1' && raw.manifest) {
    return { manifest: raw.manifest, packageMeta: { author: raw.author, notes: raw.notes } };
  }
  if (raw.id && raw.capabilities) {
    return { manifest: raw, packageMeta: null };
  }
  return { manifest: null, error: 'Expected plugin-package/v1 or extension manifest' };
}

export function validatePluginDraft(draft) {
  const errors = [];
  const id = String(draft.id || '').trim();
  const name = String(draft.name || '').trim();
  const version = String(draft.version || '').trim();

  if (!id) errors.push('Plugin id is required (e.g. com.acme.my-tools).');
  else if (!ID_PATTERN.test(id)) {
    errors.push('Plugin id must look like com.vendor.name (lowercase, dots).');
  }

  if (!name) errors.push('Plugin display name is required.');
  if (!version) errors.push('Version is required.');

  const tools = draft.tools || [];
  const skills = draft.skills || [];

  const appPanels = draft.appPanels || [];

  if (tools.length === 0 && skills.length === 0 && appPanels.length === 0) {
    errors.push('Add at least one tool, skill, or app panel.');
  }

  const capIds = new Set();
  appPanels.forEach((p, i) => {
    const pid = String(p.id || '').trim();
    const label = String(p.name || '').trim() || pid || `App panel ${i + 1}`;
    if (!pid) errors.push(`App panel ${i + 1}: id is required.`);
    else if (capIds.has(pid)) errors.push(`Duplicate app panel id: ${pid}`);
    capIds.add(pid);
    const html = String(p.html || '');
    if (!html.trim()) errors.push(`App panel "${label}": add the panel HTML.`);
    if (html.length > APP_PANEL_HTML_MAX) errors.push(`App panel "${label}": HTML is limited to ${APP_PANEL_HTML_MAX / 1000} KB.`);
    if (String(p.outputKey || '').trim() && !OUTPUT_KEY_PATTERN.test(String(p.outputKey).trim())) {
      errors.push(`App panel "${label}": the data field must be letters, numbers, or underscores.`);
    }
  });

  const toolIds = new Set();
  tools.forEach((t, i) => {
    const tid = String(t.id || '').trim();
    const tname = String(t.name || '').trim();
    if (!tid) errors.push(`Tool ${i + 1}: id is required.`);
    if (!tname) errors.push(`Tool ${i + 1}: name is required.`);
    if (tid) {
      if (toolIds.has(tid)) errors.push(`Duplicate tool id: ${tid}`);
      toolIds.add(tid);
    }
  });

  const skillIds = new Set();
  skills.forEach((s, i) => {
    const sid = String(s.id || '').trim();
    const sname = String(s.name || '').trim();
    if (!sid) errors.push(`Skill ${i + 1}: id is required.`);
    if (!sname) errors.push(`Skill ${i + 1}: name is required.`);
    if (sid) {
      if (skillIds.has(sid)) errors.push(`Duplicate skill id: ${sid}`);
      skillIds.add(sid);
    }
    if (!String(s.systemPrompt || '').trim()) {
      errors.push(`Skill "${sname || sid || i + 1}": system prompt is required for sideload (no systemPromptPath).`);
    }
  });

  const manifest = manifestFromDraft(draft);
  const hosts = manifest.permissions?.allowedHosts || [];
  (manifest.capabilities || []).flatMap((c) => (c.tool ? [c.tool] : c.tools || [])).forEach((t) => {
    if (t.source !== 'http' || !t.http?.url) return;
    let host = '';
    try {
      host = new URL(String(t.http.url).replace(/\{\{[^}]*\}\}/g, 'x')).hostname;
    } catch {
      errors.push(`Tool ${t.id}: the HTTP url is not a valid URL.`);
      return;
    }
    if (!hostAllowed(host, hosts)) errors.push(`Tool ${t.id} calls ${host}; add it to Allowed hosts.`);
  });
  if (manifest._schemaParseErrors?.length) {
    errors.push(...manifest._schemaParseErrors);
    delete manifest._schemaParseErrors;
  }
  if (!manifest.capabilities?.length && !manifest.skills?.length) {
    errors.push('Manifest must include capabilities or skills.');
  }

  return {
    valid: errors.length === 0,
    errors,
    manifest,
  };
}

export const BUILDER_TEMPLATES = {
  blank: {
    label: 'Blank plugin',
    draft: () => emptyPluginDraft(),
  },
  tools: {
    label: 'Tools pack',
    draft: () => emptyPluginDraft({
      id: 'com.example.my-tools',
      name: 'My Tools Pack',
      description: 'Tenant MCP tools for agents and mcp-tool nodes.',
      capabilityId: 'my-tools.bundle',
      tools: [{
        id: 'my_greet',
        name: 'my_greet',
        description: 'Example greeting tool for smoke tests',
        source: 'extension',
      }],
    }),
  },
  skills: {
    label: 'Skills pack',
    draft: () => emptyPluginDraft({
      id: 'com.example.my-skills',
      name: 'My Skills Pack',
      description: 'Agent skills with prompts and tool allowlists.',
      skills: [{
        ...emptySkillDraft(),
        id: 'my-assistant',
        name: 'My Assistant',
        description: 'General assistant skill for flow AI agent nodes.',
        systemPrompt: 'You are a helpful assistant. Answer clearly and cite tool results when used.',
      }],
    }),
  },
  full: {
    label: 'Tools + skills',
    draft: () => emptyPluginDraft({
      id: 'com.example.my-plugin',
      name: 'My CLAW Plugin',
      description: 'Combined MCP tools and agent skills for this tenant.',
      capabilityId: 'my-plugin.tools',
      tools: [{
        id: 'my_lookup',
        name: 'my_lookup',
        description: 'Lookup helper used by the assistant skill',
        source: 'extension',
      }],
      skills: [{
        ...emptySkillDraft(),
        id: 'my-copilot',
        name: 'My Copilot',
        description: 'Copilot skill bound to my_lookup.',
        systemPrompt: 'You are a copilot. Use my_lookup when the user asks for lookup data.',
        allowedTools: ['my_lookup'],
      }],
    }),
  },
};
